<template>
  <div class="checkout-page">
    <Spinner :show="loading"/>

    <h2>Checkout <span v-if="cartItems.length > 0" class="item-count">({{ cartItems.length }} {{ cartItems.length === 1 ? 'item' : 'items' }})</span></h2>

    <div v-if="showCancelMessage" class="notification is-danger">
      <p>Payment was canceled. Please try again or complete your purchase later.</p>
    </div>

    <template v-if="cartItems.length > 0">
      <div v-for="(item, index) in cartItems" :key="index" class="checkout-item">
        <p><strong>Thinking of U Postcard</strong></p>
        <div class="item-details">
          <div class="details">
            <p>
              <strong>Recipient:</strong>
              <span class="checkout-value">{{ item.recipientFirstName }} {{ item.recipientLastName }}</span>
            </p>
            <p>
              <strong>Tone:</strong>
              <span class="checkout-value">{{ item.tone }} </span>
            </p>
            <p>
              <strong>Style:</strong>
              <span class="checkout-value"> {{ item.style }} </span>
            </p>
          </div>
        </div>
        <button @click="toggleMoreInfo(index)" class="more-info">More Info</button>
        <div v-if="expandedIndex === index" class="expanded-info">
          <p>
            <strong>From:</strong>
            <span class="checkout-value">{{ item.fromField }}</span>
          </p>
          <p>
            <strong>Address:</strong>
            <span class="checkout-value">{{ item.address.street }}, {{ item.address.city }}, {{ item.address.state }} {{ item.address.postalCode }} </span>
          </p>
          <p>
            <strong>Additional Information:</strong>
            <span class="checkout-value">{{ item.userIntent?.trim() || "N/A" }} </span>
          </p>
          <p>
            <strong>Occasion:</strong>
            <span class="checkout-value">{{ item.occasion?.trim() || "N/A" }} </span>
          </p>
        </div>
        <div class="cart-item-actions">
          <fa :icon="['fas', 'trash-alt']" class="delete-icon" @click="confirmRemove(index)" />
        </div>
      </div>

      <div class="coupon-section">
        <label for="couponCode">Coupon Code:</label>
        <input type="text" id="couponCode" v-model="couponCode" placeholder="Enter coupon code" size="20" />
      </div>

      <div class="checkout-summary">
        <div class="order-breakdown">
          <div class="breakdown-row">
            <span>{{ cartItems.length }} {{ cartItems.length === 1 ? 'Postcard' : 'Postcards' }} x ${{ itemPrice }}</span>
            <span>${{ totalPrice }}</span>
          </div>
          <div class="breakdown-row total">
            <span>Total</span>
            <span>${{ totalPrice }}</span>
          </div>
        </div>
        <div class="checkout-buttons">
          <router-link to="/create-postcard" class="continue-shopping">
            <fa :icon="['fas', 'plus']" /> Add Another
          </router-link>
          <button
            @click="proceedToPayment"
            :disabled="loading || processing"
            class="proceed-button"
          >
            <span v-if="loading">Processing...</span>
            <span v-else>Proceed to Payment</span>
          </button>
        </div>
      </div>
    </template>

    <div v-else class="empty-cart">
      <fa :icon="['fas', 'shopping-cart']" class="empty-cart-icon" />
      <h3>Your cart is empty</h3>
      <p>Create a personalized postcard to surprise someone special.</p>
      <router-link to="/create-postcard" class="create-button">Create a Postcard</router-link>
    </div>

    <ConfirmationModal v-if="showConfirmation" @confirm="removeItem" @close="cancelRemove" />
  </div>
</template>

<script>
import { signInAnonymously } from 'firebase/auth';
import { auth } from '@/firebase';
import { createCheckoutSession } from '@/api';
// `/pure` so Stripe.js is NOT auto-injected on import (the default entrypoint
// loads the 246KB render-blocking script on every page for fraud detection).
// It now loads lazily on the first loadStripe() call — i.e. only at checkout.
import { loadStripe } from '@stripe/stripe-js/pure';
import { track, USD, POSTCARD_ITEM } from '@/utils/track';
import { mapActions, mapGetters } from 'vuex';
import { FontAwesomeIcon as fa } from '@fortawesome/vue-fontawesome';
import ConfirmationModal from './ConfirmationModal.vue';
import Spinner from './SpinnerComp.vue';

export default {
  name: 'CheckoutPage',
  components: { fa, ConfirmationModal, Spinner },
  created() {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('status') === 'canceled') {
      this.showCancelMessage = true;
    }
  },
  data() {
    return {
      showConfirmation: false,
      itemToRemove: null,
      expandedIndex: null,
      showCancelMessage: false,
      loading: false,
      processing: false,
      couponCode: "",
    };
  },
  computed: {
    ...mapGetters(['cartItems', 'totalPrice', 'itemPrice']),
  },
  methods: {
    ...mapActions(['removeFromCart']),
    async proceedToPayment() {
      // Re-entry guard: ignore additional clicks once a checkout is in flight.
      // `processing` stays set through the Stripe redirect (it is only cleared
      // on a caught error before redirect), so the button cannot fire twice.
      if (this.processing) return;
      this.processing = true;
      this.loading = true;
      track('begin_checkout', {
        currency: USD,
        value: this.totalPrice,
        items: [{ ...POSTCARD_ITEM, price: this.itemPrice, quantity: this.cartItems.length }],
      });
      try {
        // Only sign in anonymously when there is no current user, so a
        // logged-in buyer's session (and their Stripe customer) is preserved.
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }
      } catch (error) {
        this.processing = false;
        this.loading = false;
        this.$toast.error("Sign in failed: " + error.message);
        return;
      }
      try {

        const { data: sessionData } = await createCheckoutSession({
          successUrl: window.location.origin + '/success',
          cancelUrl: window.location.origin + '/checkout?status=canceled',
          quantity: this.cartItems.length,
          couponCode: this.couponCode || null
        });

        // Redirect to Stripe Checkout. Intentionally do NOT reset `processing`
        // on success — the navigation away should be the terminal state, so the
        // button stays disabled until the redirect settles.
        const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
        await stripe.redirectToCheckout({sessionId: sessionData.id});
      } catch (error) {
        // Redirect failed or session creation failed — allow a retry.
        this.processing = false;
        this.loading = false;
        this.$toast.error("Payment error: " + error.message);
      }
    },
    confirmRemove(index) {
      this.itemToRemove = index;
      this.showConfirmation = true;
    },
    toggleMoreInfo(index) {
      this.expandedIndex = this.expandedIndex === index ? null : index;
    },
    removeItem() {
      if (this.itemToRemove !== null) {
        this.removeFromCart(this.itemToRemove);
      }
      this.showConfirmation = false;
      this.itemToRemove = null;
    },
    cancelRemove() {
      this.showConfirmation = false;
      this.itemToRemove = null;
    },
  }
};
</script>

<style scoped>
.checkout-page {
  padding: 30px;
  max-width: 800px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

h2 {
  text-align: center;
  color: var(--hollywood-cerise);
  margin-bottom: 24px;
  font-size: 2rem;
}

.item-count {
  font-size: 1rem;
  font-weight: 400;
  color: var(--violet-lush);
}

.checkout-item {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #eee;
  margin: 12px auto 0;
  align-items: center;
  text-align: center;
  padding-bottom: 20px;
}

.checkout-value {
  margin-left: 6px;
  color: var(--violet-lush);
}

.item-details {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-start;
  padding-top: 6px;
}

.delete-icon {
  color: var(--hollywood-cerise);
  cursor: pointer;
  font-size: 1.4rem;
  margin-top: 10px;
  transition: color 0.3s;
}
.delete-icon:hover {
  color: var(--violet-lush);
}

.more-info {
  background-color: var(--hollywood-cerise);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.95rem;
  margin: 12px auto 0;
  transition: background-color 0.3s ease;
  width: 140px;
  text-align: center;
  white-space: nowrap;
}

.more-info:hover {
  background-color: var(--violet-lush);
}

.expanded-info {
  margin-top: 12px;
  font-size: 0.95rem;
  background-color: var(--light-pink);
  padding: 10px;
  border-radius: 6px;
}

.checkout-summary {
  margin-top: 40px;
  text-align: center;
}

.order-breakdown {
  max-width: 300px;
  margin: 0 auto 20px;
  padding: 16px;
  background-color: var(--light-pink);
  border-radius: 8px;
}

.breakdown-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  color: var(--rich-black);
}

.breakdown-row.total {
  border-top: 2px solid var(--hollywood-cerise);
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--hollywood-cerise);
}

.proceed-button {
  background-color: var(--hollywood-cerise);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.proceed-button:hover {
  background-color: var(--violet-lush);
}

.proceed-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.checkout-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.continue-shopping {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 24px;
  border: 2px solid var(--hollywood-cerise);
  border-radius: 25px;
  color: var(--hollywood-cerise);
  text-decoration: none;
  font-size: 1rem;
  transition: all 0.3s;
}

.continue-shopping:hover {
  background-color: var(--hollywood-cerise);
  color: white;
}

.coupon-section {
  margin-top: 20px;
  text-align: center;
}
.coupon-section label {
  font-weight: 500;
  margin-right: 10px;
}
.coupon-section input {
  width: 200px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid var(--powder-blue);
  background-color: var(--light-pink);
  color: var(--rich-black);
}

.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}

.empty-cart-icon {
  font-size: 4rem;
  color: var(--powder-blue);
  margin-bottom: 20px;
}

.empty-cart h3 {
  color: var(--hollywood-cerise);
  font-size: 1.5rem;
  margin-bottom: 12px;
}

.empty-cart p {
  color: var(--rich-black);
  margin-bottom: 24px;
}

.create-button {
  background-color: var(--hollywood-cerise);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s;
}

.create-button:hover {
  background-color: var(--violet-lush);
}

@media (max-width: 767px) {
  .checkout-page {
    padding: 16px;
  }

  h2 {
    font-size: 1.6rem;
  }

  .item-details {
    flex-direction: column;
    align-items: flex-start;
  }

  .proceed-button {
    width: 100%;
    padding: 10px;
  }

  .more-info {
    width: 100%;
    text-align: center;
  }

  .create-button {
    width: 100%;
    text-align: center;
  }
}
</style>
